import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Sparkles, Upload, ImageIcon, X, Loader2, ThumbsUp, AlertCircle,
  Wrench, ClipboardCheck, Share2, Bookmark, Info, Crown,
} from "lucide-react";

const BAKE_TYPES = [
  { key: "sourdough",     label: "Sourdough" },
  { key: "sandwich",      label: "Sandwich bread" },
  { key: "focaccia",      label: "Focaccia" },
  { key: "baguette",      label: "Baguette" },
  { key: "cake_pop",      label: "Cake pop" },
  { key: "cookies",       label: "Cookies" },
  { key: "cake",          label: "Cake" },
  { key: "muffins",       label: "Muffins" },
  { key: "other",         label: "Other" },
];

export default function CrumbAnalyzer() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [bakeType, setBakeType] = useState("sourdough");
  const [hydration, setHydration] = useState("");
  const [proofing, setProofing] = useState("");
  const [flour, setFlour] = useState("");
  const [notes, setNotes] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    api.get("/crumb/quota").then((r) => setQuota(r.data));
  }, [user, loading, nav]);

  const acceptFile = (f) => {
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
      toast.error("Please upload a JPEG, PNG, or WEBP image.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => { setFile(null); setPreview(null); setResult(null); };

  const analyze = async () => {
    if (!file) { toast.error("Please upload a photo first"); return; }
    if (quota && !quota.is_premium && quota.remaining <= 0) {
      toast.error("You've hit the free tier limit. Upgrade to premium for unlimited analyses.");
      return;
    }
    setAnalyzing(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bake_type", bakeType);
      if (hydration) fd.append("hydration", hydration);
      if (proofing) fd.append("proofing", proofing);
      if (flour) fd.append("flour", flour);
      if (notes) fd.append("notes", notes);
      const { data } = await api.post("/crumb/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setResult(data);
      // refresh quota
      api.get("/crumb/quota").then((r) => setQuota(r.data));
      toast.success("Analysis saved to My Bakes");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAnalyzing(false); }
  };

  const postToForum = async () => {
    if (!result?.id) return;
    try {
      const { data } = await api.post(`/crumb/${result.id}/post`);
      toast.success("Posted to forum!");
      nav(`/p/${data.post_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading || !user) return null;
  const a = result?.analysis;
  const remaining = quota?.is_premium ? "unlimited" : `${quota?.remaining ?? "…"} of ${quota?.limit ?? 3}`;

  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              <Sparkles className="w-3.5 h-3.5" style={{ color: "#F4A83B" }} />
              <span>Premium tool</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              AI <span style={{ color: "#F4A83B" }}>Crumb</span> Analyzer
            </h1>
            <p className="mt-2 text-[#857163] max-w-2xl">
              Upload a photo of your bake and get an instant, expert-style analysis: what went right, what to fix, and how to nail your next bake.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs uppercase tracking-widest text-[#857163]">This month</div>
            <div className="font-heading text-2xl text-[#3B2C24] mt-0.5">
              {quota?.is_premium ? <span className="inline-flex items-center gap-1"><Crown className="w-5 h-5" style={{ color: "#F4A83B" }} /> Unlimited</span> : remaining}
            </div>
            <Link to="/my-bakes" className="text-xs text-[#FF7E67] hover:underline" data-testid="link-my-bakes">View My Bakes →</Link>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: Upload + form */}
          <div className="cp-card p-5 sm:p-6" data-testid="analyzer-panel">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => !preview && inputRef.current?.click()}
              className="rounded-2xl border-2 border-dashed transition cursor-pointer overflow-hidden"
              style={{
                borderColor: dragging ? "#F4A83B" : "#EBDCD1",
                background: dragging ? "rgba(244,168,59,0.06)" : "#F9EFE6",
                minHeight: 260,
              }}
              data-testid="drop-zone"
            >
              <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => acceptFile(e.target.files?.[0])} className="hidden" data-testid="file-input" />
              {preview ? (
                <div className="relative">
                  <img src={preview} alt="preview" className="w-full max-h-[420px] object-contain bg-[#F9EFE6]" />
                  <button
                    onClick={(e) => { e.stopPropagation(); clearFile(); }}
                    className="absolute top-3 right-3 bg-white/90 rounded-full p-1.5 shadow hover:scale-105 transition"
                    data-testid="clear-image-btn"
                    aria-label="Remove image"
                  ><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="p-10 flex flex-col items-center justify-center text-center">
                  <div className="w-14 h-14 rounded-full grid place-items-center mb-3" style={{ background: "rgba(244,168,59,0.15)" }}>
                    <Upload className="w-6 h-6" style={{ color: "#F4A83B" }} />
                  </div>
                  <div className="font-heading text-xl text-[#3B2C24]">Drop a photo of your bake</div>
                  <div className="text-sm text-[#857163] mt-1">or click to browse — JPEG, PNG, WEBP up to 8MB</div>
                </div>
              )}
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <label className="block sm:col-span-2">
                <span className="text-xs text-[#857163]">Bake type</span>
                <select className="cp-input mt-1" value={bakeType} onChange={(e) => setBakeType(e.target.value)} data-testid="bake-type-select">
                  {BAKE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Hydration % (optional)</span>
                <input className="cp-input mt-1" placeholder="72%" value={hydration} onChange={(e) => setHydration(e.target.value)} data-testid="hydration-input" />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Proofing time / temp (optional)</span>
                <input className="cp-input mt-1" placeholder="8h @ 72°F" value={proofing} onChange={(e) => setProofing(e.target.value)} data-testid="proofing-input" />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Flour used (optional)</span>
                <input className="cp-input mt-1" placeholder="Bread flour + 10% whole wheat" value={flour} onChange={(e) => setFlour(e.target.value)} data-testid="flour-input" />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Notes (optional)</span>
                <input className="cp-input mt-1" placeholder="24h cold retard, dutch oven" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="notes-input" />
              </label>
            </div>

            <button
              onClick={analyze}
              disabled={!file || analyzing || (quota && !quota.is_premium && quota.remaining <= 0)}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-white font-semibold text-base transition hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, #F4A83B 0%, #E8842B 100%)" }}
              data-testid="analyze-btn"
            >
              {analyzing ? (<><Loader2 className="w-5 h-5 animate-spin" /> Analyzing your bake…</>) : (<><Sparkles className="w-5 h-5" /> Analyze Crumb</>)}
            </button>

            <div className="text-[11px] text-[#857163] mt-3 leading-relaxed flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>This is AI-assisted advice, not a substitute for hands-on experience. Results improve with clearer photos and more context.</span>
            </div>
          </div>

          {/* Right: Results */}
          <div className="cp-card p-5 sm:p-6 min-h-[400px]" data-testid="results-panel">
            {!result && !analyzing && (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-16 h-16 rounded-full grid place-items-center mb-4" style={{ background: "rgba(244,168,59,0.10)" }}>
                  <ClipboardCheck className="w-7 h-7" style={{ color: "#F4A83B" }} />
                </div>
                <div className="font-heading text-2xl text-[#3B2C24]">Your analysis will show here</div>
                <p className="text-sm text-[#857163] mt-2 max-w-sm">
                  Add a photo, fill in optional context, and hit <b>Analyze Crumb</b>.
                </p>
              </div>
            )}
            {analyzing && (
              <div className="h-full flex flex-col items-center justify-center py-16">
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: "#F4A83B" }} />
                <div className="mt-4 font-heading text-lg text-[#3B2C24]">Reading your crumb…</div>
                <div className="text-xs text-[#857163] mt-1">This usually takes 5–15 seconds</div>
              </div>
            )}
            {result && a && (
              <div className="space-y-5" data-testid="analysis-result">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#857163]">Diagnosis</div>
                    <h2 className="font-heading text-2xl text-[#3B2C24] mt-1 leading-tight">{a.diagnosis}</h2>
                    {a.bake_type_detected && (
                      <div className="text-xs text-[#857163] mt-1">Identified: {a.bake_type_detected}</div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-[#857163]">Confidence</div>
                    <div className="font-heading text-3xl font-bold" style={{ color: "#F4A83B" }}>{a.confidence ?? "—"}%</div>
                  </div>
                </div>

                {typeof a.quality_score === "number" && (
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-[#857163]">Overall quality</span>
                      <span className="font-semibold text-[#3B2C24]">{a.quality_score}/100</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#F9EFE6] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${a.quality_score}%`, background: "linear-gradient(90deg, #F4A83B, #E8842B)" }} />
                    </div>
                  </div>
                )}

                {a.positives?.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-[#3B2C24] mb-2 inline-flex items-center gap-1.5"><ThumbsUp className="w-4 h-4" style={{ color: "#4C9F70" }} /> What's working</div>
                    <ul className="space-y-1.5">
                      {a.positives.map((p, i) => (
                        <li key={i} className="text-sm text-[#3B2C24] flex gap-2"><span style={{ color: "#4C9F70" }}>✓</span><span>{p}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {a.causes?.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-[#3B2C24] mb-2 inline-flex items-center gap-1.5"><AlertCircle className="w-4 h-4" style={{ color: "#C2185B" }} /> Possible causes</div>
                    <ul className="space-y-1.5">
                      {a.causes.map((c, i) => (
                        <li key={i} className="text-sm text-[#3B2C24] flex gap-2"><span style={{ color: "#C2185B" }}>•</span><span>{c}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {a.recommendations?.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-[#3B2C24] mb-2 inline-flex items-center gap-1.5"><Wrench className="w-4 h-4" style={{ color: "#F4A83B" }} /> Recommendations for next bake</div>
                    <ul className="space-y-1.5">
                      {a.recommendations.map((r, i) => (
                        <li key={i} className="text-sm text-[#3B2C24] flex gap-2"><span style={{ color: "#F4A83B" }}>→</span><span>{r}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2 border-t border-[#EBDCD1]">
                  <Link
                    to="/my-bakes"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-[#3B2C24] text-white hover:opacity-90 transition"
                    data-testid="view-in-bakes-btn"
                  ><Bookmark className="w-4 h-4" /> View in My Bakes</Link>
                  <button
                    onClick={postToForum}
                    disabled={result.posted_to_forum}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6] transition disabled:opacity-50"
                    data-testid="post-to-forum-btn"
                  ><Share2 className="w-4 h-4" /> {result.posted_to_forum ? "Already posted" : "Post to Forum"}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
